import React from 'react';

import { useIntl } from 'react-intl';

import { PATH } from 'src/features/navigation/utils/constants';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { useGetSystemComponent } from 'src/features/systems/api/systemComponents/useGetSystemComponent';
import { useParking } from 'src/providers/ParkingProvider';
import { Translation } from 'src/types';
import { systemsMessages } from 'src/features/systems/utils/messages';
import {
    ALIGN_TRANSLATION_MAP,
    COMPONENT_DATA_TYPE_TRANSLATIONS_MAP,
    ComponentBadgeOptions,
    ComponentTypeBadgeOptions,
    GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP,
    IDLE_STATE_TRANSLATIONS_MAP,
    LISTEN_STATE_TRANSLATIONS_MAP,
    ORIENTATION_TRANSLATIONS_MAP,
    POSITION_TRANSLATIONS_MAP,
    SYSTEM_COMPONENT_TRANSLATIONS_MAP,
    SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP,
    TICKET_PRINTER_TYPE_TRANSLATION_MAP,
    isBackendServiceComponent,
} from 'src/features/systems/utils/constants';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { ICON_MAP } from 'src/utils/constants';
import { GATEWAY_DISPLAY_MODEL, SYSTEM_COMPONENT, TICKET_PRINTER_ROW_TYPE } from 'src/features/systems/utils/types';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { Separator } from 'src/registry/ui/Separator';
import { GeneralBlockHeader, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import SidePanelAttribute from 'src/features/stay/components/sidePanel/SidePanelAttribute';

type ComponentSidePanelProps = {
    componentId: string;
};

export default function ComponentSidePanel({ componentId }: ComponentSidePanelProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const permissionAccessGatewayPass = useHasPermissions([PERMISSION.ACCESS_GATEWAY_PASSES]);
    const permissionZones = useHasPermissions([PERMISSION.MANAGE_ZONES]);

    const { data, isLoading } = useGetSystemComponent({
        componentId: componentId,
        parkingId,
        config: {
            enabled: !!componentId,
        },
    });

    const translate = (translations: Translation[]) =>
        translations.find((translation) => translation.language === locale);

    const zone = React.useMemo(() => {
        const zone = data?.zone;
        if (!data || !zone?.translations) return '-';
        return `${translate(zone.translations)?.name}`;
    }, [data, translate]);

    const gateway = React.useMemo(() => {
        const gateway = data?.gateway;
        if (!data || !gateway?.translations) return '-';
        return `${translate(gateway.translations)?.name}`;
    }, [data, translate]);

    const payStation = React.useMemo(() => {
        const payStation = data?.pay_station;
        if (!data || !payStation?.name) return '-';
        return payStation?.name;
    }, [data]);

    const isFiscalDevice = data?.component === SYSTEM_COMPONENT.FISCAL_DEVICE;
    const isGatewayDisplay = data?.component === SYSTEM_COMPONENT.GATEWAY_DISPLAY;
    const isTicketPrinter = data?.component === SYSTEM_COMPONENT.TICKET_PRINTER;
    const isCashOrCardApi =
        data?.component === SYSTEM_COMPONENT.CASH_API || data?.component === SYSTEM_COMPONENT.CARD_API;
    const isBackendService = isBackendServiceComponent(data?.component);

    return (
        <>
            {data ? (
                <DialogHeader className="flex-row">
                    <DialogTitle className="flex items-center gap-2 pr-0 font-mono">
                        {formatMessage(systemsMessages.component)}
                    </DialogTitle>
                    <span>{data.name}</span>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}
            <DialogSeparator />
            <DialogBody>
                <GeneralBlockAttribute label={formatMessage(systemsMessages.component)}>
                    <Badge theme={data?.component ? ComponentBadgeOptions[data.component].theme : 'gray'}>
                        {data?.component && (
                            <FontAwesomeIcon icon={ComponentBadgeOptions[data.component].icon as IconProp} />
                        )}

                        {data?.component ? formatMessage(SYSTEM_COMPONENT_TRANSLATIONS_MAP[data.component]) : '-'}
                    </Badge>
                </GeneralBlockAttribute>
                <GeneralBlockAttribute label={formatMessage(systemsMessages.code)} value={data?.code} />
                {!isBackendService && (
                    <>
                        <GeneralBlockAttribute label={formatMessage(systemsMessages.type)} isLoading={isLoading}>
                            <Badge
                                theme={
                                    data?.component_type
                                        ? ComponentTypeBadgeOptions[data.component_type].theme
                                        : 'gray'
                                }
                            >
                                {data?.component_type && (
                                    <FontAwesomeIcon
                                        icon={ComponentTypeBadgeOptions[data.component_type].icon as IconProp}
                                    />
                                )}
                                {data?.component_type
                                    ? formatMessage(SYSTEM_COMPONENT_TYPE_TRANSLATIONS_MAP[data.component_type])
                                    : '-'}
                            </Badge>
                        </GeneralBlockAttribute>
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.zone)}
                            value={zone}
                            link={
                                permissionZones && data?.zone?.id
                                    ? `/${PATH.ADMINISTRATION}/zone/${data.zone.id}`
                                    : ''
                            }
                            icon={ICON_MAP.ARROW_UP}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.gateway)}
                            value={gateway}
                            link={
                                permissionAccessGatewayPass && data?.gateway?.id
                                    ? `/${PATH.ADMINISTRATION}/gateway/${data.gateway.id}`
                                    : ''
                            }
                            icon={ICON_MAP.ARROW_UP}
                        />
                    </>
                )}

                {!isFiscalDevice && !isGatewayDisplay && !isCashOrCardApi && !isBackendService && !isTicketPrinter && (
                    <GeneralBlockAttribute
                        label={formatMessage(systemsMessages.model)}
                        value={data?.data?.model ? data.data.model : '-'}
                    />
                )}

                {isFiscalDevice && (
                    <>
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.componentType)}
                            value={
                                data?.data?.type
                                    ? formatMessage(COMPONENT_DATA_TYPE_TRANSLATIONS_MAP[data.data.type])
                                    : '-'
                            }
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.apiUrl)}
                            value={data?.data?.api_url ? data.data.api_url : '-'}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.apiKey)}
                            value={data?.data?.api_key ? data.data.api_key : '-'}
                        />
                    </>
                )}

                {isGatewayDisplay && (
                    <>
                        <GeneralBlockAttribute
                            label={formatMessage(paymentsMessages.payStation)}
                            value={data?.pay_station ? data?.pay_station?.name : '-'}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.displayModel)}
                            value={
                                data?.data?.display_model
                                    ? formatMessage(GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP[data?.data?.display_model])
                                    : '-'
                            }
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.displayServerIP)}
                            value={data?.data?.display_server_ip ? data?.data?.display_server_ip : '-'}
                        />

                        {data.data?.display_model === GATEWAY_DISPLAY_MODEL.SP && (
                            <>
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.displayServerApiKey)}
                                    value={data?.data?.display_server_api_key ? data.data.display_server_api_key : '-'}
                                />
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.idleState)}
                                    value={
                                        data?.data?.idle_state
                                            ? formatMessage(IDLE_STATE_TRANSLATIONS_MAP[data.data.idle_state])
                                            : '-'
                                    }
                                />
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.orientation)}
                                    value={
                                        data?.data?.orientation
                                            ? formatMessage(ORIENTATION_TRANSLATIONS_MAP[data.data.orientation])
                                            : '-'
                                    }
                                />
                            </>
                        )}
                        {data.data?.display_model === GATEWAY_DISPLAY_MODEL.HUIDU && (
                            <>
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.screenWidth)}
                                    value={data?.data?.display_width ? data?.data?.display_width : '-'}
                                />
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.screenHeight)}
                                    value={data?.data?.display_height ? data?.data?.display_height : '-'}
                                />
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.titleFontSize)}
                                    value={
                                        data?.data?.display_title_font_size ? data?.data?.display_title_font_size : '-'
                                    }
                                />
                                <GeneralBlockAttribute
                                    label={formatMessage(systemsMessages.subtitleFontSize)}
                                    value={
                                        data?.data?.display_subtitle_font_size
                                            ? data?.data?.display_subtitle_font_size
                                            : '-'
                                    }
                                />
                            </>
                        )}
                    </>
                )}

                {data?.component &&
                    !isGatewayDisplay &&
                    !isFiscalDevice &&
                    !isCashOrCardApi &&
                    !isBackendService &&
                    !isTicketPrinter && (
                    <>
                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.componentType)}
                            value={
                                data?.data?.type
                                    ? formatMessage(COMPONENT_DATA_TYPE_TRANSLATIONS_MAP[data.data.type])
                                    : '-'
                            }
                        />

                        <GeneralBlockAttribute
                            label={formatMessage(systemsMessages.make)}
                            value={data?.data?.make ? data.data.make : '-'}
                        />
                    </>
                )}

                {isCashOrCardApi && (
                    <GeneralBlockAttribute label={formatMessage(paymentsMessages.payStation)} value={payStation} />
                )}

                {data && (
                    <>
                        {Object.hasOwn(data.data!, 'rtsp_stream_url') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.RTSPStreamUrl)}
                                value={data.data?.rtsp_stream_url}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'channel') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.channel)}
                                value={data.data?.channel}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'persistent_channel') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.persistentChannel)}
                                value={data.data?.persistent_channel}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'open_duration') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.pulseDuration)}
                                value={data.data?.open_duration}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'topic_code') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.topicCode)}
                                value={data.data?.topic_code}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'listen_state') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.listenState)}
                                value={
                                    data?.data?.listen_state
                                        ? formatMessage(LISTEN_STATE_TRANSLATIONS_MAP[data?.data?.listen_state])
                                        : '-'
                                }
                            />
                        )}
                        {data.data && Object.hasOwn(data.data, 'position') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.position)}
                                value={
                                    data?.data?.position
                                        ? formatMessage(POSITION_TRANSLATIONS_MAP[data?.data?.position])
                                        : '-'
                                }
                            />
                        )}
                        {Object.hasOwn(data.data!, 'status_channel') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.statusChannel)}
                                value={data.data?.status_channel ?? '-'}
                            />
                        )}
                        {Object.hasOwn(data.data!, 'status_listen_state') && (
                            <GeneralBlockAttribute
                                label={formatMessage(systemsMessages.statusListenState)}
                                value={
                                    data?.data?.status_listen_state
                                        ? formatMessage(LISTEN_STATE_TRANSLATIONS_MAP[data?.data?.status_listen_state])
                                        : '-'
                                }
                            />
                        )}

                        {isTicketPrinter &&
                            data.data &&
                            Object.hasOwn(data.data, 'payload') &&
                            data?.data?.payload?.map((p, index) => (
                                <React.Fragment key={index}>
                                    <Separator />
                                    <GeneralBlockHeader variant="dashboard">
                                        <GeneralBlockTitle>
                                            {formatMessage(systemsMessages.row, { number: index + 1 })}
                                        </GeneralBlockTitle>
                                    </GeneralBlockHeader>

                                    <div className="flex flex-col gap-2">
                                        <SidePanelAttribute
                                            label={formatMessage(systemsMessages.align)}
                                            value={p?.align ? formatMessage(ALIGN_TRANSLATION_MAP[p?.align]) : '-'}
                                            className="flex flex-row items-center gap-2"
                                        />
                                        <SidePanelAttribute
                                            label={formatMessage(systemsMessages.type)}
                                            value={
                                                p?.type
                                                    ? formatMessage(TICKET_PRINTER_TYPE_TRANSLATION_MAP[p?.type])
                                                    : '-'
                                            }
                                            className="flex flex-row items-center gap-2"
                                        />
                                        {p.type === TICKET_PRINTER_ROW_TYPE.TEXT && (
                                            <SidePanelAttribute
                                                label={formatMessage(systemsMessages.text)}
                                                value={p?.text ? p.text : '-'}
                                                className="flex flex-row items-center gap-2"
                                            />
                                        )}
                                    </div>
                                </React.Fragment>
                            ))}
                    </>
                )}
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
